import React from "react";
import { useNavigate, useLocation } from "react-router";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { IconDefinitionConstants, RouteConstants } from "utils/constants";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { QuotationBannerProps } from "models/propTypes/quotationPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DialogEnums } from "models/enums";
//#endregion
//#region components
import { Banner, ChoiceChips, StyledButton, useMountedState } from "components/common";
import { Grid } from "@mui/material";
import { displayDialog } from "store/actions/utilActions";
import { useQuotesService } from "serviceHooks";
//#endregion
import { CSSHelper, DateHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const QuotationBanner: React.FC<QuotationBannerProps> = (props) => {
  const quotesService = useQuotesService();
  const { quotation } = props;
  const { getMounted } = useMountedState();
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const [sentDate, setSentDate] = React.useState<Date>(props.quotation.date ?? new Date());

  const onQuotationEditConfirm = () => {
    return quotesService.changeState(
      props.quotation.serialNumber,
      WintSuperkollAPIModel.ModifiableQuotationState.NotSent
    );
  };

  const openQuotationEditWindow = () => {
    navigate(
      UrlHelper.formatPath(
        `/Quotation/${props.quotation.serialNumber}?${RouteConstants.QuotationQueryParams.IsEdit}=true`
      ),
      {
        replace: true,
        state: { background: location },
      }
    );
  };
  const editQuotationClicked = () => {
    if (quotation.quotationState !== WintSuperkollAPIModel.QuotationApiState.NotSent) {
      dispatch(
        displayDialog({
          isOpen: false,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit),
          titleIcon: IconDefinitionConstants.WCommentDots,
          dialogType: DialogEnums.DialogType.Message,
          actions: [
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
              onClick: onQuotationEditConfirm,
            },
          ],
          children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.ChangeStateBeforeEdit),
          onSubmit: openQuotationEditWindow,
        })
      );
    } else {
      openQuotationEditWindow();
    }
  };
  React.useEffect(() => {
    quotesService.getStateLogs(quotation.serialNumber).then((items) => {
      if (getMounted()) {
        const lastSent = items.reverse().find((i) => i.status === WintSuperkollAPIModel.QuotationApiState.Sent);
        lastSent && lastSent.date && setSentDate(lastSent.date);
      }
    });
  });

  const selectionStates: SelectionItem[] = [
    {
      value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.Created),
      id: WintSuperkollAPIModel.ModifiableQuotationState.NotSent,
      data: WintSuperkollAPIModel.ModifiableQuotationState.NotSent,
    },
    {
      value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Sent),
      id: WintSuperkollAPIModel.ModifiableQuotationState.Sent,
      data: WintSuperkollAPIModel.ModifiableQuotationState.Sent,
    },
    {
      value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.Won),
      id: WintSuperkollAPIModel.ModifiableQuotationState.Won,
      data: WintSuperkollAPIModel.ModifiableQuotationState.Won,
    },
    {
      value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.Lost),
      id: WintSuperkollAPIModel.ModifiableQuotationState.Lost,
      data: WintSuperkollAPIModel.ModifiableQuotationState.Lost,
    },
  ];

  const changeStateFunc = (item: SelectionItem) => {
    return quotesService.changeState(
      props.quotation.serialNumber,
      item.data as WintSuperkollAPIModel.ModifiableQuotationState
    );
  };

  const onStateChange = (item: SelectionItem) => {
    props.reloadHandler && props.reloadHandler(() => changeStateFunc(item));
  };

  return (
    <Banner
      title={LocalizationHelperInstance.getFormattedMessage(
        quotesService.checkQuotationExpired(quotation)
          ? TranslationKeys.Quotation.BannerTitleOverdue
          : `Quotation.BannerTitleState_${quotation.quotationState}`,
        "",
        { days: DateHelper.getDiffDays(sentDate, new Date()).toString() }
      )}
      icon={IconDefinitionConstants.WEdit}
      type={props.displayType}
    >
      <Grid container direction="column" spacing={3}>
        <Grid item>
          {LocalizationHelperInstance.getFormattedMessage(
            quotesService.checkQuotationExpired(quotation)
              ? TranslationKeys.Quotation.BannerHeaderOverdue
              : `Quotation.BannerHeaderState_${quotation.quotationState}`,
            "",
            { customerName: quotation.customerName ?? "" }
          )}
        </Grid>

        {quotation.quotationState !== WintSuperkollAPIModel.QuotationApiState.InvoiceCreated && (
          <>
            <Grid
              item
              direction="column"
              className={clsx(cssExports.invertedInputForm, CSSHelper.getClassByDisplayType(props.displayType))}
            >
              <Grid>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.ChangeStatus)}</Grid>
              <Grid>
                <ChoiceChips items={selectionStates} onChange={onStateChange} title={""}></ChoiceChips>
              </Grid>
            </Grid>
            <Grid item>
              <StyledButton size="sm" displayType={props.displayType} onClick={editQuotationClicked}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.EditQuotation)}
              </StyledButton>
            </Grid>
          </>
        )}
      </Grid>
    </Banner>
  );
};

export default QuotationBanner;
